import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Loader2, User, FileText, Car, Users as UsersIcon } from "lucide-react";
import { CommandDialog, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from "@/components/ui/command";

export default function GlobalSearch({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setOpen]);

  useEffect(() => {
    if (!q || q.length < 2) { setRes(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then(({ data }) => setRes(data)).catch(() => setRes(null)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search customers, leads, policies, vehicles..." value={q} onValueChange={setQ} data-testid="universal-search-input" />
      <CommandList>
        {loading && <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>}
        {!loading && q.length >= 2 && res && !res.customers?.length && !res.leads?.length && !res.policies?.length && !res.vehicles?.length && (
          <CommandEmpty>No results for "{q}"</CommandEmpty>
        )}
        {res?.customers?.length > 0 && (
          <CommandGroup heading="Customers">
            {res.customers.map((c) => (
              <CommandItem key={c.id} value={"cust" + c.id} onSelect={() => go(`/customers/${c.id}`)}>
                <User className="h-4 w-4 mr-2 text-primary" /> {c.name}
                <span className="ml-2 font-mono text-xs text-muted-foreground">{c.customer_id} · {c.primary_mobile}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res?.leads?.length > 0 && (
          <CommandGroup heading="Leads">
            {res.leads.map((l) => (
              <CommandItem key={l.id} value={"lead" + l.id} onSelect={() => go(`/leads`)}>
                <UsersIcon className="h-4 w-4 mr-2 text-indigo-500" /> {l.name}
                <span className="ml-2 font-mono text-xs text-muted-foreground">{l.lead_id} · {l.mobile}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res?.policies?.length > 0 && (
          <CommandGroup heading="Policies">
            {res.policies.map((p) => (
              <CommandItem key={p.id} value={"pol" + p.id} onSelect={() => go(p.customer_id ? `/customers/${p.customer_id}` : "/policies")}>
                <FileText className="h-4 w-4 mr-2 text-emerald-500" /> <span className="font-mono text-xs">{p.policy_number}</span>
                <span className="ml-2 text-xs text-muted-foreground">{p.insurer}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res?.vehicles?.length > 0 && (
          <CommandGroup heading="Vehicles">
            {res.vehicles.map((v) => (
              <CommandItem key={v.id} value={"veh" + v.id} onSelect={() => go(v.customer_id ? `/customers/${v.customer_id}` : "/customers")}>
                <Car className="h-4 w-4 mr-2 text-sky-500" /> <span className="font-mono text-xs">{v.registration_number}</span>
                <span className="ml-2 text-xs text-muted-foreground">{v.make} {v.model}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
